import {
	IFilterDefinition,
	ISelectFilterDefinition,
	ITextFilterDefinition,
	OptionType
} from '@evinced-private/ui-common';

import { DEFAULT_UNIT_ID_DISPLAY_NAME } from 'src/common/consts/platformConfigConsts';
import { DEFAULT_PERIOD_OPTION } from 'src/common/helpers/DataFilterHelper';
import { DevelopmentToggles, getSingleToggle } from 'src/common/services/TogglesService';

import {
	ENVIRONMENT_FILTER_LABELS,
	LABELS_FILTER_LABELS,
	PROPERTIES_FILTER_LABELS,
	SEARCH_FILTER_LABELS,
	TEST_PERIOD_FILTER_LABELS,
	UNIT_ID_FILTER_LABELS
} from '../../types/filter-section-v2';

import { useWebSDKFiltersStore } from './store';

interface SelectAllOption extends OptionType {
	isSelectAll?: boolean;
}

const enableWebSdkProperties = getSingleToggle(DevelopmentToggles.ENABLE_WEB_SDK_PROPERTIES);

const searchFilter: ITextFilterDefinition = {
	id: SEARCH_FILTER_LABELS.id,
	label: SEARCH_FILTER_LABELS.label,
	filterType: 'text',
	defaultValue: '',
	componentProps: {}
};

const propertiesFilter: ISelectFilterDefinition = {
	id: PROPERTIES_FILTER_LABELS.id,
	label: PROPERTIES_FILTER_LABELS.label,
	filterType: 'select',
	defaultValue: [],
	componentProps: {
		placeholder: PROPERTIES_FILTER_LABELS.placeholder,
		someItemsSelectedText: PROPERTIES_FILTER_LABELS.someItemsSelectedText,
		isMultiple: true,
		className: PROPERTIES_FILTER_LABELS.className,
		id: PROPERTIES_FILTER_LABELS.id,
		label: PROPERTIES_FILTER_LABELS.componentLabel,
		listboxWidth: PROPERTIES_FILTER_LABELS.listboxWidth
	}
};

export const getWebSdkTestsFilterDefinitionsV2 = (
	unitIdDisplayName: string = DEFAULT_UNIT_ID_DISPLAY_NAME,
	labelsLoadOptions?: (searchText: string, callback: (options: OptionType[]) => void) => void,
	labelsOptions?: OptionType[],
	isSelectAllLabels = false
): IFilterDefinition[] => {
	const environmentFilter: ISelectFilterDefinition = {
		id: ENVIRONMENT_FILTER_LABELS.id,
		label: ENVIRONMENT_FILTER_LABELS.label,
		filterType: 'select',
		defaultValue: [],
		componentProps: {
			placeholder: ENVIRONMENT_FILTER_LABELS.placeholder,
			someItemsSelectedText: ENVIRONMENT_FILTER_LABELS.someItemsSelectedText,
			isMultiple: true,
			className: ENVIRONMENT_FILTER_LABELS.className,
			id: ENVIRONMENT_FILTER_LABELS.id,
			label: ENVIRONMENT_FILTER_LABELS.componentLabel,
			listboxWidth: '500px'
		}
	};

	const unitIdFilter: ISelectFilterDefinition = {
		id: UNIT_ID_FILTER_LABELS.id,
		label: UNIT_ID_FILTER_LABELS.label,
		filterType: 'select',
		defaultValue: [],
		componentProps: {
			placeholder: UNIT_ID_FILTER_LABELS.placeholderPrefix,
			allSelectedPlaceholder: `${UNIT_ID_FILTER_LABELS.allSelectedPrefix} ${unitIdDisplayName}s`,
			someItemsSelectedText: UNIT_ID_FILTER_LABELS.someItemsSelectedPrefix(unitIdDisplayName),
			isMultiple: true,
			className: UNIT_ID_FILTER_LABELS.className,
			id: UNIT_ID_FILTER_LABELS.id,
			label: unitIdDisplayName
		}
	};

	const wrappedLoadOptions = labelsLoadOptions
		? (searchText: string, callback: (options: OptionType[]) => void): void => {
				const currentState = useWebSDKFiltersStore.getState();
				// Update store if search term changed (works for empty string too)
				if (currentState.labelsSearchTerm !== searchText) {
					currentState.setLabelsSearchTerm(searchText);
				}
				labelsLoadOptions(searchText, callback);
			}
		: undefined;

	const labelsFilter: ISelectFilterDefinition = {
		id: LABELS_FILTER_LABELS.id,
		label: LABELS_FILTER_LABELS.label,
		filterType: 'select',
		defaultValue: [],
		onChange: (data) => {
			const selectedData = data as SelectAllOption[];
			const hasSelectAll = selectedData.some((item) => item.isSelectAll);
			const labelsToStore = selectedData.filter((item) => !item.isSelectAll);

			const state = useWebSDKFiltersStore.getState();

			if (state.isSelectAllLabels !== hasSelectAll) {
				state.setSelectAllLabels(hasSelectAll);
			}

			const currentValues = new Set(state.selectedLabels.map((item) => item.value));
			const newValues = labelsToStore.map((item) => item.value);
			const hasChanged =
				currentValues.size !== newValues.length || newValues.some((v) => !currentValues.has(v));

			if (hasChanged) {
				state.setSelectedLabels(labelsToStore);
			}
		},
		componentProps: {
			autoSelectNewOptions: true,
			placeholder: LABELS_FILTER_LABELS.placeholder,
			someItemsSelectedText: isSelectAllLabels
				? LABELS_FILTER_LABELS.allLabels
				: LABELS_FILTER_LABELS.someItemsSelectedText,
			isMultiple: true,
			maxSelectedItems: 20,
			isAllOptionsVisible: true,
			className: LABELS_FILTER_LABELS.className,
			id: LABELS_FILTER_LABELS.id,
			label: LABELS_FILTER_LABELS.componentLabel,
			...(wrappedLoadOptions && { loadOptions: wrappedLoadOptions }),
			...(labelsOptions && { options: labelsOptions }),
			...(wrappedLoadOptions && { isSearchable: true })
		}
	};

	const testPeriodFilter: ISelectFilterDefinition = {
		id: TEST_PERIOD_FILTER_LABELS.id,
		label: TEST_PERIOD_FILTER_LABELS.label,
		filterType: 'select',
		defaultValue: [DEFAULT_PERIOD_OPTION],
		componentProps: {
			placeholder: TEST_PERIOD_FILTER_LABELS.placeholder,
			isMultiple: false,
			id: TEST_PERIOD_FILTER_LABELS.id,
			label: TEST_PERIOD_FILTER_LABELS.componentLabel
		}
	};

	return [
		searchFilter,
		unitIdFilter,
		environmentFilter,
		labelsFilter,
		...(enableWebSdkProperties ? [propertiesFilter] : []),
		testPeriodFilter
	];
};
